// CommoditySelectionPage.jsx - Tower-specific commodity selection page

import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Container,
  Grid,
  alpha,
  Snackbar,
  Alert,
  Button,
  Tabs,
  Tab,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SidebarLayout from "../shared/sections/sidebar/SidebarLayout";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoard";
import InventoryIcon from "@mui/icons-material/Inventory";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import SpaIcon from "@mui/icons-material/Spa";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { useAuth } from "../context/AuthContext";
import { useAppWorkspaceStatus } from "../hooks/useAppWorkspaceStatus";
import {
  APP_WORKSPACE_MODEL_TYPE,
  APP_WORKSPACE_TOWERS,
  MECHANICAL_COMMODITY_ORDER,
  filterCommoditiesByModelTab,
  getDefaultTowerModelTab,
  getTowerModelTabs,
  isPerlabParametricCommodity,
  isWorkspaceItemPublished,
} from "../utils/appWorkspaceConfig";

const towerIconMap = {
  mechanical: <PrecisionManufacturingIcon sx={{ fontSize: 40 }} />,
  electronics: <DeveloperBoardIcon sx={{ fontSize: 40 }} />,
  packaging: <InventoryIcon sx={{ fontSize: 40 }} />,
  fnb: <RestaurantIcon sx={{ fontSize: 40 }} />,
  bpch: <SpaIcon sx={{ fontSize: 40 }} />,
};

const CommoditySelectionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { statusMap, loading: workspaceLoading, error: workspaceError, refreshStatuses } = useAppWorkspaceStatus();
  const workspaceReady = !workspaceLoading && !workspaceError;
  const brandColor = "#ff6034";
  const [requestNoticeOpen, setRequestNoticeOpen] = useState(false);
  const [activeModelTab, setActiveModelTab] = useState(APP_WORKSPACE_MODEL_TYPE.AI_POWERED);
  const normalizedRole = (() => {
    if (Array.isArray(user?.role) && user.role.length > 0) {
      return String(user.role[0]?.role_name || "")
        .trim()
        .toLowerCase();
    }
    return String(user?.role || "")
      .trim()
      .toLowerCase();
  })();
  const isSuperAdmin = normalizedRole === "super admin";

  const hasAccess = (category, commodityName) => {
    if (!user?.appAccess || user.appAccess.length === 0) {
      return false;
    }

    const accessKey = `${category} - ${commodityName}`;
    return user.appAccess.includes(accessKey);
  };

  const getViewAccessList = () => {
    return Array.isArray(user?.viewAccess) ? user.viewAccess : null;
  };

  const viewAccessList = getViewAccessList();
  const applyViewFilter = Array.isArray(user?.viewAccess);
  const hasViewAccess = (category, commodityName) => {
    if (!applyViewFilter) return true;
    const accessKey = `${category} - ${commodityName}`;
    return (viewAccessList || []).includes(accessKey);
  };

  const pptColors = {
    background: "#191A1B",
    surface: "#242728",
    border: "rgba(241, 242, 243, 0.08)",
    textPrimary: "#F1F2F3",
    textSecondary: "#798286",
    white: "#FFFFFF",
  };

  const pptTypography = {
    family: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
    title: {
      fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
      fontWeight: 100,
      fontSize: "40px",
      lineHeight: "40px",
      letterSpacing: "0.3px",
    },
    headline: {
      fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
      fontWeight: 500,
      fontSize: "16px",
      lineHeight: "24px",
      letterSpacing: "0.3px",
    },
    paragraph: {
      fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
      fontWeight: 400,
      fontSize: "16px",
      lineHeight: "24px",
      letterSpacing: "0.3px",
    },
  };

  const tower = location.pathname.split("/")[1];
  const currentTowerConfig = APP_WORKSPACE_TOWERS.find((towerConfig) => towerConfig.id === tower);
  const currentTower = currentTowerConfig
    ? {
        ...currentTowerConfig,
        icon: towerIconMap[currentTowerConfig.id],
      }
    : null;

  if (!currentTower) {
    return (
      <SidebarLayout>
        <Container>
          <Typography variant="h4">Tower not found</Typography>
        </Container>
      </SidebarLayout>
    );
  }

  const visibleCommodities = applyViewFilter
    ? currentTower.commodities.filter((commodity) =>
        hasViewAccess(currentTower.title, commodity.name),
      )
    : currentTower.commodities;
  const availableModelTabs = getTowerModelTabs(visibleCommodities);
  const availableModelTabIds = availableModelTabs.map((tab) => tab.id).join("|");
  const displayedCommodities = (() => {
    const filteredCommodities = filterCommoditiesByModelTab(visibleCommodities, activeModelTab);

    if (tower !== "mechanical") {
      return filteredCommodities;
    }

    return filteredCommodities.sort(
      (a, b) => MECHANICAL_COMMODITY_ORDER.indexOf(a.name) - MECHANICAL_COMMODITY_ORDER.indexOf(b.name),
    );
  })();

  useEffect(() => {
    setActiveModelTab(getDefaultTowerModelTab(visibleCommodities));
  }, [tower, availableModelTabIds]);

  useEffect(() => {
    if (
      availableModelTabs.length > 0 &&
      !availableModelTabs.some((tab) => tab.id === activeModelTab)
    ) {
      setActiveModelTab(getDefaultTowerModelTab(visibleCommodities));
    }
  }, [activeModelTab, availableModelTabIds, visibleCommodities]);

  const handleCommodityClick = (commodityName, commodity = null) => {
    const towerRoute = `/${tower}`;

    if (towerRoute === "/mechanical") {
      if (commodityName === "Casting & Machining") {
        navigate("/mechanical/casting");
        return;
      }

      if (commodityName === "Forging") {
        navigate("/mechanical/forging");
        return;
      }

      if (commodityName === "Sheet Metal") {
        if (commodity?.hasAI) {
          navigate("/mechanical/sheetmetal?ai=true");
        } else {
          navigate("/mechanical/sheetmetal");
        }
        return;
      } else if (commodityName === "Plastics") {
        if (commodity?.hasAI) {
          navigate("/mechanical/costing?commodity=Plastics&ai=true");
        } else {
          navigate("/mechanical/costing?commodity=Plastics");
        }
        return;
      }
    }

    if (towerRoute === "/packaging") {
      if (commodityName === "Laminates") {
        navigate("/packaging/laminates");
        return;
      } else if (commodityName === "Corrugated") {
        navigate("/packaging/costing");
        return;
      } else if (commodityName === "Bottles") {
        navigate("/packaging/glassbottles");
        return;
      }
    }

    if (towerRoute === "/fnb") {
      navigate(`/fnb/generic?tower=FB&commodity=${encodeURIComponent(commodityName)}`);
      return;
    }

    if (towerRoute === "/bpch") {
      navigate(`/bpch/generic?tower=BPCH&commodity=${encodeURIComponent(commodityName)}`);
      return;
    }

    navigate(`${currentTower.route}/costing?commodity=${encodeURIComponent(commodityName)}`);
  };

  const handleRequestAccess = () => {
    setRequestNoticeOpen(true);
  };

  const handleModelTabChange = (event, newValue) => {
    setActiveModelTab(newValue);
  };

  return (
    <SidebarLayout>
      <Box
        sx={{
          minHeight: "100vh",
          background: pptColors.background,
          py: 4,
          "& .MuiTypography-root": {
            fontFamily: pptTypography.family,
            letterSpacing: "0.3px",
          },
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              mb: 4,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `linear-gradient(135deg, ${currentTower.color}, ${alpha(
                  currentTower.color,
                  0.6,
                )})`,
                boxShadow: `0 8px 24px ${alpha(currentTower.color, 0.3)}`,
              }}
            >
              {currentTower.icon}
            </Box>
            <Box>
              <Typography
                variant="h3"
                sx={{
                  ...pptTypography.title,
                  color: pptColors.textPrimary,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                }}
              >
                {currentTower.title}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  ...pptTypography.paragraph,
                  color: pptColors.textSecondary,
                  fontSize: { xs: "0.9rem", md: "1rem" },
                }}
              >
                Select a commodity to start costing
              </Typography>
            </Box>
          </Box>

          {workspaceLoading && <Typography role="status" sx={{ mb: 2 }}>Loading available tools…</Typography>}
          {workspaceError && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" onClick={refreshStatuses}>Retry</Button>}>
            Available tools could not be loaded. Retry to continue.
          </Alert>}
          {availableModelTabs.length > 0 ? (
            <Box sx={{ mb: 3 }}>
              <Tabs
                value={activeModelTab}
                onChange={handleModelTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  minHeight: 44,
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#F1F2F3",
                    height: 3,
                    borderRadius: "3px 3px 0 0",
                  },
                  "& .MuiTab-root": {
                    ...pptTypography.paragraph,
                    textTransform: "none",
                    minHeight: 44,
                    minWidth: "unset",
                    px: 0,
                    mr: 4,
                    color: pptColors.textSecondary,
                    alignItems: "flex-start",
                    "&.Mui-selected": {
                      color: pptColors.textPrimary,
                    },
                  },
                }}
              >
                {availableModelTabs.map((tab) => (
                  <Tab
                    key={tab.id}
                    value={tab.id}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            color:
                              tab.id === APP_WORKSPACE_MODEL_TYPE.AI_POWERED
                                ? "#ff7b4a"
                                : "#22d3ee",
                          }}
                        >
                          {tab.id === APP_WORKSPACE_MODEL_TYPE.AI_POWERED ? (
                            <AutoAwesomeIcon sx={{ fontSize: "0.9rem" }} />
                          ) : (
                            <AccountTreeIcon sx={{ fontSize: "0.9rem" }} />
                          )}
                        </Box>
                        <Typography
                          sx={{
                            ...pptTypography.paragraph,
                            fontSize: "0.9rem",
                            lineHeight: 1.2,
                          }}
                        >
                          {tab.label}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </Tabs>
            </Box>
          ) : null}

          {visibleCommodities.length === 0 ? (
            <Typography sx={{ color: pptColors.textSecondary }}>
              No commodities available for your view access.
            </Typography>
          ) : displayedCommodities.length === 0 ? (
            <Typography sx={{ color: pptColors.textSecondary }}>
              No commodities available for this model.
            </Typography>
          ) : (
            <Grid container spacing={3}>
              {displayedCommodities.map((commodity, index) => {
                const userHasAccess = hasAccess(currentTower.title, commodity.name);
                const showPPMIcon = isPerlabParametricCommodity(commodity);
                const commodityPublished = isWorkspaceItemPublished(
                  statusMap,
                  currentTower.title,
                  commodity.name,
                  commodity.modelType,
                );
                const canOpenComingSoon = !commodityPublished && isSuperAdmin;
                const cardClickable = workspaceReady && ((commodityPublished && userHasAccess) || canOpenComingSoon);
                const showHoverEffect = true;

                if (!userHasAccess) {
                  const accessKey = `${currentTower.title} - ${commodity.name}`;
                  console.log(
                    `Disabled: User "${user?.email || "Unknown"}" does not have access to "${accessKey}"`,
                  );
                }

                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                    <Card
                      aria-disabled={!cardClickable}
                      onClick={() => {
                        if (cardClickable) {
                          handleCommodityClick(commodity.name, commodity);
                        }
                      }}
                      sx={{
                        height: "100%",
                        width: "280px",
                        maxWidth: "100%",
                        cursor: cardClickable ? "pointer" : "default",
                        borderRadius: 3,
                        border: `2px solid ${pptColors.border}`,
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        position: "relative",
                        overflow: "hidden",
                        opacity: userHasAccess ? 1 : 0.82,
                        background: pptColors.surface,
                        boxShadow: `0 2px 8px ${alpha("#000", 0.2)}`,
                        "&:hover": showHoverEffect
                          ? {
                              transform: "translateY(-8px)",
                              boxShadow: `0 12px 24px ${alpha("#F1F2F3", 0.3)}`,
                              borderColor: "#F1F2F3",
                              "& .arrow-icon": {
                                transform: "translateX(4px)",
                                opacity: 1,
                              },
                            }
                          : {},
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: 10,
                          right: 10,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: 22,
                          height: 22,
                          px: 0,
                          borderRadius: showPPMIcon ? 1 : "50%",
                          backgroundColor: showPPMIcon
                            ? "rgba(8, 145, 178, 0.14)"
                            : "transparent",
                          border: "none",
                        }}
                      >
                        {showPPMIcon ? (
                          <AccountTreeIcon
                            sx={{
                              fontSize: "0.85rem",
                              color: "#22d3ee",
                            }}
                          />
                        ) : (
                          <AutoAwesomeIcon
                            sx={{
                              fontSize: "0.9rem",
                              color: "#ff7b4a",
                            }}
                          />
                        )}
                      </Box>
                      <CardContent
                        sx={{ p: 1.5, display: "flex", gap: 2, alignItems: "flex-start" }}
                      >
                        <Box
                          className="commodity-icon"
                          sx={{
                            fontSize: "2rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 120,
                            height: 120,
                            minWidth: 120,
                            borderRadius: 2,
                            background: alpha(brandColor, 0.15),
                            transition: "all 0.3s ease",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={commodity.icon}
                            alt={commodity.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        </Box>

                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            minHeight: 120,
                          }}
                        >
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{
                                ...pptTypography.headline,
                                mb: 0.5,
                                color: pptColors.textPrimary,
                                fontSize: "0.9rem",
                              }}
                            >
                              {commodity.name}
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                ...pptTypography.paragraph,
                                color: pptColors.textSecondary,
                                lineHeight: 1.4,
                                fontSize: "0.75rem",
                              }}
                            >
                              {commodity.description}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {!commodityPublished ? (
                              <Typography
                                variant="button"
                                sx={{
                                  ...pptTypography.paragraph,
                                  color: "#f5c04c",
                                  fontSize: "0.7rem",
                                  fontWeight: 700,
                                  letterSpacing: "0.4px",
                                }}
                              >
                                Coming Soon
                              </Typography>
                            ) : userHasAccess ? (
                              <Typography
                                variant="button"
                                sx={{
                                  ...pptTypography.paragraph,
                                  color: "#2ecc71",
                                  fontSize: "0.7rem",
                                  fontWeight: 500,
                                  letterSpacing: "0.4px",
                                  cursor: "pointer",
                                }}
                              >
                                Start Costing
                              </Typography>
                            ) : (
                              <Typography
                                variant="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleRequestAccess();
                                }}
                                sx={{
                                  ...pptTypography.paragraph,
                                  color: brandColor,
                                  fontSize: "0.7rem",
                                  fontWeight: 700,
                                  letterSpacing: "0.4px",
                                  cursor: "pointer",
                                }}
                              >
                                Request Access
                              </Typography>
                            )}
                            {commodityPublished ? (
                              <ArrowForwardIcon
                                className="arrow-icon"
                                sx={{
                                  fontSize: 16,
                                  color: userHasAccess ? "#2ecc71" : brandColor,
                                  transition: "all 0.3s ease",
                                  opacity: 0.7,
                                }}
                              />
                            ) : null}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Container>
        <Snackbar
          open={requestNoticeOpen}
          autoHideDuration={4000}
          onClose={() => setRequestNoticeOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={() => setRequestNoticeOpen(false)} severity="info" sx={{ width: "100%" }}>
            Request submitted.
          </Alert>
        </Snackbar>
      </Box>
    </SidebarLayout>
  );
};

export default CommoditySelectionPage;

